import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePregnancy } from "../PregnancyContext";
import { buildPregnancyNavigationStack } from "../buildPregnancyNavigationStack";
import type { PregnancyStackParamList } from "../PregnancyNavigator";

export function PregnancyUsageScreen() {
  const t = useTranslations();
  const { state, updateState, save } = usePregnancy();
  const navigation = useNavigation<NavigationProp<PregnancyStackParamList>>();

  const selection = useMemo(() => {
    if (state.hasBeenPregnant == null) return null;
    return state.hasBeenPregnant ? "yes" : "no";
  }, [state.hasBeenPregnant]);

  const handleChange = (value: "yes" | "no") => {
    if (value === "no") {
      // Clear pregnancy data
      const idsToDelete = state.pregnancies
        .filter((p) => p.id)
        .map((p) => p.id!);
      const anonymousToDelete = state.pregnancies.filter((p) => !p.id).length;
      updateState({
        hasBeenPregnant: false,
        pregnancies: [],
        pregnancyIdsToDelete: [...state.pregnancyIdsToDelete, ...idsToDelete],
        anonymousPregnanciesToDelete:
          state.anonymousPregnanciesToDelete + anonymousToDelete,
      });
    } else {
      updateState({
        hasBeenPregnant: true,
        // If no pregnancies exist yet, start with one empty entry
        pregnancies: state.pregnancies.length > 0 ? state.pregnancies : [{}],
      });
    }
  };

  const handleSave = async () => {
    await save();
    const stack = buildPregnancyNavigationStack(state);
    const currentIdx = stack.indexOf("PregnancyUsage");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.pregnancies.usage_title")}
      subtitle={t("med_history.gynae.pregnancies.usage_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.pregnancies.next")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => handleChange("yes")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() => handleChange("no")}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
});
